import React from 'react';
import { X, CheckCircle, XCircle, Clock, Copy, Zap, Tv, Calendar, Hash, DollarSign, Info, Home } from 'lucide-react';
import { toast } from 'react-hot-toast';

const BillTransactionDetailsModal = ({ transaction, onClose }) => {
  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 2,
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const transactionDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    
    const diffTime = today - transactionDate;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return `Today, ${date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}`;
    } else if (diffDays === 1) {
      return `Yesterday, ${date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}`;
    } else {
      return date.toLocaleDateString('en-US', { 
        month: 'long', 
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'SUCCESS':
        return <CheckCircle className="h-5 w-5 text-green-600" />;
      case 'PENDING':
        return <Clock className="h-5 w-5 text-yellow-600" />;
      case 'FAILED':
        return <XCircle className="h-5 w-5 text-red-600" />;
      default:
        return null;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'SUCCESS':
        return 'bg-green-50 text-green-700 border-green-200';
      case 'PENDING':
        return 'bg-yellow-50 text-yellow-700 border-yellow-200';
      case 'FAILED':
        return 'bg-red-50 text-red-700 border-red-200';
      default:
        return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const getBillTypeIcon = (billType) => {
    switch (billType) {
      case 'ELECTRICITY':
        return <Zap className="h-5 w-5 text-white" />;
      case 'TV_SUBSCRIPTION':
        return <Tv className="h-5 w-5 text-white" />;
      default:
        return <Info className="h-5 w-5 text-white" />;
    }
  };

  const getBillTypeBg = (billType) => {
    switch (billType) {
      case 'ELECTRICITY':
        return 'bg-gradient-to-br from-yellow-500 to-yellow-600';
      case 'TV_SUBSCRIPTION':
        return 'bg-gradient-to-br from-purple-500 to-purple-600';
      default:
        return 'bg-gradient-to-br from-gray-500 to-gray-600';
    }
  };

  const formatToken = (token) => {
    if (!token) return '';
    return token.match(/.{1,4}/g)?.join('-') || token;
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-3 sm:px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose}></div>

        <div className="inline-block align-bottom bg-white rounded-xl px-4 pt-5 pb-4 text-left overflow-hidden shadow-neomorph transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full sm:p-6 border border-primary-100/30 w-full max-w-[95%] sm:max-w-2xl">
          <div className="absolute top-0 right-0 pt-3 pr-3 sm:pt-4 sm:pr-4">
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 bg-[#E8ECF5] rounded-lg p-1.5 shadow-neomorph-inset"
            >
              <X className="h-4 w-4 sm:h-5 sm:w-5" />
            </button>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-start gap-3">
            <div className={`mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 sm:h-14 sm:w-14 rounded-xl shadow-lg ${getBillTypeBg(transaction.billType)}`}>
              {getBillTypeIcon(transaction.billType)}
            </div>
            <div className="text-center sm:text-left sm:mt-0 w-full">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <div>
                  <div className="flex items-center justify-center sm:justify-start mb-1">
                    <div className="w-1 h-4 sm:h-5 bg-gradient-to-b from-primary-400 to-primary-600 rounded-full mr-2" />
                    <h3 className="text-base sm:text-lg font-semibold text-gray-800">
                      Bill Transaction Details
                    </h3>
                  </div>
                  <p className="text-xs sm:text-sm text-gray-500">
                    {transaction.billType?.replace('_', ' ')} payment
                  </p>
                </div>
                <span className={`inline-flex items-center px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg text-xs font-medium border ${getStatusColor(transaction.status)} shadow-neomorph-inset w-fit mx-auto sm:mx-0`}>
                  {getStatusIcon(transaction.status)}
                  <span className="ml-1.5">{transaction.status}</span>
                </span>
              </div>
            </div>
          </div>

          <div className="mt-5 sm:mt-6 space-y-4 sm:space-y-5">
            {/* Transaction Info Grid */}
            <div className="flex flex-col md:flex-row gap-4">
              {/* Left Column */}
              <div className="flex-1 space-y-4">
                <div className="bg-[#E8ECF5] rounded-xl p-3 sm:p-4 shadow-neomorph-inset">
                  <h4 className="text-xs font-medium text-gray-600 mb-3 flex items-center">
                    <Info className="h-3.5 w-3.5 mr-1.5 text-gray-500" />
                    Transaction Information
                  </h4>
                  <div className="space-y-3">
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Reference</p>
                      <div className="flex items-center justify-between gap-2 bg-white rounded-lg p-2 shadow-neomorph-inset">
                        <p className="text-xs font-mono text-gray-800 break-all flex-1">
                          {transaction.vtuReference || transaction.id}
                        </p>
                        <button
                          onClick={() => handleCopy(transaction.vtuReference || transaction.id)}
                          className="text-gray-400 hover:text-gray-600 bg-[#E8ECF5] rounded-md p-1 shadow-neomorph flex-shrink-0"
                        >
                          <Copy className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Provider Reference</p>
                      <p className="text-xs font-mono text-gray-800 bg-white rounded-lg p-2 shadow-neomorph-inset break-all">
                        {transaction.providerReference || 'N/A'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Date & Time</p>
                      <div className="flex items-center bg-white rounded-lg p-2 shadow-neomorph-inset">
                        <Calendar className="h-3.5 w-3.5 text-gray-400 mr-2 flex-shrink-0" />
                        <p className="text-xs text-gray-800 break-words">{formatDate(transaction.createdAt)}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-[#E8ECF5] rounded-xl p-3 sm:p-4 shadow-neomorph-inset">
                  <h4 className="text-xs font-medium text-gray-600 mb-3 flex items-center">
                    <Home className="h-3.5 w-3.5 mr-1.5 text-gray-500" />
                    Recipient Details
                  </h4>
                  <div className="space-y-3">
                    <div>
                      <p className="text-xs text-gray-500 mb-1">
                        {transaction.billType === 'ELECTRICITY' ? 'Meter Number' : 'Smartcard Number'}
                      </p>
                      <p className="text-sm font-medium text-gray-800 bg-white rounded-lg p-2 shadow-neomorph-inset break-all">
                        {transaction.recipient}
                      </p>
                    </div>
                    {transaction.productDetails?.customerName && (
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Customer Name</p>
                        <p className="text-sm font-medium text-gray-800 bg-white rounded-lg p-2 shadow-neomorph-inset break-all">
                          {transaction.productDetails.customerName}
                        </p>
                      </div>
                    )}
                    {transaction.productDetails?.provider && (
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Provider</p>
                        <p className="text-sm font-medium text-gray-800 bg-white rounded-lg p-2 shadow-neomorph-inset">
                          {transaction.productDetails.provider}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Right Column */}
              <div className="flex-1 space-y-4">
                <div className="bg-[#E8ECF5] rounded-xl p-3 sm:p-4 shadow-neomorph-inset">
                  <h4 className="text-xs font-medium text-gray-600 mb-3 flex items-center">
                    <DollarSign className="h-3.5 w-3.5 mr-1.5 text-gray-500" />
                    Amount Details
                  </h4>
                  <div className="space-y-3">
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Amount</p>
                      <p className="text-xl sm:text-2xl font-bold text-primary-600 bg-white rounded-lg p-3 shadow-neomorph-inset break-all">
                        {formatAmount(transaction.amount)}
                      </p>
                    </div>
                    {transaction.productDetails?.packageName && (
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Package</p>
                        <p className="text-sm text-gray-800 bg-white rounded-lg p-2 shadow-neomorph-inset break-all">
                          {transaction.productDetails.packageName}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Electricity Token Section */}
                {transaction.billType === 'ELECTRICITY' && transaction.receipt?.token && (
                  <div className="bg-yellow-50 rounded-xl p-3 sm:p-4 shadow-neomorph-inset border border-yellow-200/50">
                    <h4 className="text-xs font-medium text-yellow-800 mb-3 flex items-center">
                      <Zap className="h-3.5 w-3.5 mr-1.5 text-yellow-600" />
                      Electricity Token
                    </h4>
                    <div className="space-y-3">
                      <div className="text-center">
                        <div className="text-base sm:text-2xl font-mono font-bold text-gray-800 bg-white rounded-lg p-3 shadow-neomorph-inset mb-2 break-all">
                          {formatToken(transaction.receipt.token)}
                        </div>
                        <button
                          onClick={() => handleCopy(transaction.receipt.token)}
                          className="inline-flex items-center text-xs text-yellow-600 hover:text-yellow-700 bg-yellow-100 rounded-lg px-3 py-1.5 shadow-neomorph-inset"
                        >
                          <Copy className="h-3.5 w-3.5 mr-1.5" />
                          Copy Token
                        </button>
                      </div>
                      <p className="text-xs text-yellow-700 text-center">
                        Enter this token into your prepaid meter to recharge your electricity
                      </p>
                    </div>
                  </div>
                )}

                {/* Receipt Information */}
                {transaction.receipt && (
                  <div className="bg-[#E8ECF5] rounded-xl p-3 sm:p-4 shadow-neomorph-inset">
                    <h4 className="text-xs font-medium text-gray-600 mb-3 flex items-center">
                      <Hash className="h-3.5 w-3.5 mr-1.5 text-gray-500" />
                      Receipt Information
                    </h4>
                    
                    <div className="grid grid-cols-2 gap-2">
                      {transaction.receipt.token && transaction.billType === 'ELECTRICITY' && (
                        <div className="col-span-2 bg-white rounded-lg p-2 shadow-neomorph-inset">
                          <p className="text-xs text-gray-500 mb-1">Token</p>
                          <p className="text-xs sm:text-sm font-mono text-gray-800 break-all">{formatToken(transaction.receipt.token)}</p>
                        </div>
                      )}
                      
                      {transaction.receipt.transactionId && (
                        <div className="col-span-2 bg-white rounded-lg p-2 shadow-neomorph-inset">
                          <p className="text-xs text-gray-500 mb-1">Transaction ID</p>
                          <div className="flex items-center justify-between gap-2">
                            <p className="text-xs sm:text-sm font-mono text-gray-800 break-all flex-1">{transaction.receipt.transactionId}</p>
                            <button
                              onClick={() => handleCopy(transaction.receipt.transactionId)}
                              className="text-gray-400 hover:text-gray-600 bg-[#E8ECF5] rounded-md p-1 shadow-neomorph flex-shrink-0"
                            >
                              <Copy className="h-3 w-3" />
                            </button>
                          </div>
                        </div>
                      )}
                      
                      {transaction.receipt.amount && (
                        <div className="bg-white rounded-lg p-2 shadow-neomorph-inset">
                          <p className="text-xs text-gray-500 mb-1">Amount Paid</p>
                          <p className="text-xs sm:text-sm font-medium text-gray-800">₦{transaction.receipt.amount}</p>
                        </div>
                      )}
                      
                      {transaction.receipt.units && (
                        <div className="bg-white rounded-lg p-2 shadow-neomorph-inset">
                          <p className="text-xs text-gray-500 mb-1">Units</p>
                          <p className="text-xs sm:text-sm font-medium text-gray-800">{transaction.receipt.units}</p>
                        </div>
                      )}
                      
                      {transaction.receipt.description && (
                        <div className="col-span-2 bg-white rounded-lg p-2 shadow-neomorph-inset">
                          <p className="text-xs text-gray-500 mb-1">Description</p>
                          <p className="text-xs sm:text-sm text-gray-800 break-words">{transaction.receipt.description}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Additional Metadata */}
            {transaction.metadata && Object.keys(transaction.metadata).length > 0 && (
              <div className="bg-[#E8ECF5] rounded-xl p-3 sm:p-4 shadow-neomorph-inset">
                <h4 className="text-xs font-medium text-gray-600 mb-3">Additional Information</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {Object.entries(transaction.metadata).map(([key, value]) => (
                    typeof value !== 'object' && (
                      <div key={key} className="bg-white rounded-lg p-2 shadow-neomorph-inset">
                        <p className="text-xs text-gray-500 mb-1 capitalize break-words">
                          {key.replace(/([A-Z])/g, ' $1').trim()}
                        </p>
                        <p className="text-xs text-gray-800 break-words">{String(value)}</p>
                      </div>
                    )
                  ))}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 pt-2">
              {transaction.status === 'FAILED' && (
                <button
                  onClick={() => {
                    // Handle retry
                    onClose();
                  }}
                  className="w-full sm:w-auto px-4 py-2.5 bg-gradient-to-r from-primary-600 to-primary-700 rounded-lg shadow-md shadow-primary-500/30 hover:shadow-lg hover:shadow-primary-500/40 transition-all text-sm font-medium text-white"
                >
                  Retry Transaction
                </button>
              )}
              <button
                onClick={onClose}
                className="w-full sm:w-auto px-4 py-2.5 bg-white rounded-lg shadow-neomorph hover:shadow-neomorph-hover transition-all text-sm font-medium text-gray-700 border border-gray-100"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .shadow-neomorph {
          box-shadow: 6px 6px 11px #d0d6e0, -6px -6px 11px #ffffff;
        }
        .shadow-neomorph-hover {
          box-shadow: 4px 4px 7px #d0d6e0, -4px -4px 7px #ffffff;
        }
        .shadow-neomorph-inset {
          box-shadow: inset 2.5px 2.5px 5px #d0d6e0, inset -2.5px -2.5px 5px #ffffff;
        }
      `}</style>
    </div>
  );
};

export default BillTransactionDetailsModal;